import Big from 'big.js';
import invariant from 'tiny-invariant';

export const BigCalculator = {
  add: <T extends number | null>(a: T, b: T): T => {
    if (typeof a !== 'number' || typeof b !== 'number') {
      return null as T;
    }
    return addSafely(a, b) as T;
  },
  subtract: <T extends number | null>(a: T, b: T): T => {
    if (typeof a !== 'number' || typeof b !== 'number') {
      return null as T;
    }
    return subtractSafely(a, b) as T;
  },
  multiply: <T extends number | null>(a: T, b: T): T => {
    if (typeof a !== 'number' || typeof b !== 'number') {
      return null as T;
    }
    return multiplySafely(a, b) as T;
  },
  divide: <T extends number | null>(a: T, b: T): T | null => {
    if (typeof a !== 'number' || !b) {
      return null;
    }
    return divideSafely(a, b) as T;
  },
  sum: (numbers: number[]): number => {
    return sumSafely(numbers);
  },
};

const addSafely = (a: number, b: number): number => {
  return new Big(a).plus(b).toNumber();
};

const subtractSafely = (a: number, b: number): number => {
  return new Big(a).minus(b).toNumber();
};

const multiplySafely = (a: number, b: number): number => {
  return new Big(a).times(b).toNumber();
};

const divideSafely = (a: number, b: number): number => {
  return new Big(a).div(b).toNumber();
};

const sumSafely = (numbers: number[]): number => {
  return numbers.reduce((acc, num) => addSafely(acc, num), 0);
};

export const floorToDecimalPlaces = (
  value: number,
  decimalPlaces: number,
): number => {
  invariant(decimalPlaces >= 0, 'decimalPlaces must be 0 or greater');

  const factor = 10 ** decimalPlaces;
  return (
    BigCalculator.divide(
      Math.floor(BigCalculator.multiply(value, factor)),
      factor,
    ) ?? 0
  );
};

/**
 * Round up the value to the nearest unit.
 */
export const ceilToUnit = (value: number, unit: number): number => {
  return BigCalculator.multiply(
    Math.ceil(BigCalculator.divide(value, unit) ?? 0),
    unit,
  );
};
